import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ConfirmModalToggleRow from "./ConfirmModalToggleRow";

/**
 * 確認用モーダル（募集停止などに使える）
 *
 * @param {boolean} open - モーダルの表示状態
 * @param {function} onClose - 閉じる処理
 * @param {function} onConfirm - 確定処理
 * @param {string} title - モーダルタイトル
 * @param {string} message - 説明メッセージ
 * @param {string} subMessage - サブメッセージ
 * @param {string} confirmLabel - 確定ボタンの文言
 * @param {React.ReactNode} [toggleIcon] - トグル左側に表示するアイコン（任意）
 * @param {string} [toggleLabel] - トグルのラベル（これがある場合にトグル表示）
 * @param {string} [toggleDescription] - トグルの説明文（任意）
 * @param {boolean} [toggleValue=false] - トグルのON/OFF状態
 * @param {function} [onToggleChange] - トグル変更時のコールバック（(checked: boolean) => void）
 */
const ConfirmStopModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  subMessage = "実行してもよろしいですか？",
  confirmLabel,
  isSubmitting = false,
  toggleIcon,
  toggleLabel,
  toggleDescription,
  toggleValue = false,
  onToggleChange,
}) => {
  const theme = useTheme();
  // モバイル判定
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // toggleLabelがあればトグルを表示
  const showToggle =
    Boolean(toggleLabel) && typeof onToggleChange === "function";

  // isSubmittingがtrueのときはモーダルを閉じられないようにする
  const handleClose = (event, reason) => {
    if (isSubmitting) return;

    onClose(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1, whiteSpace: "pre-line" }}>
          {message}
        </Typography>
        <Typography>{subMessage}</Typography>
        {showToggle && (
          <ConfirmModalToggleRow
            icon={toggleIcon}
            label={toggleLabel}
            description={toggleDescription}
            value={toggleValue}
            onChange={onToggleChange}
          />
        )}
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={{
          px: isMobile ? 1 : 3,
          pb: 2,
          gap: isMobile ? 2 : 2,
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{
            borderRadius: 9999,
            px: isMobile ? 1 : 3,
            fontWeight: "bold",
            minWidth: isMobile ? "100%" : 120,
            minHeight: isMobile ? 56 : undefined,
            fontSize: isMobile ? "1rem" : undefined,
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isSubmitting}
          variant="contained"
          sx={{
            borderRadius: 9999,
            px: isMobile ? 1 : 3,
            fontWeight: "bold",
            bgcolor: "#000",
            color: "#fff",
            minWidth: isMobile ? "100%" : 140,
            minHeight: isMobile ? 56 : undefined,
            fontSize: isMobile ? "1rem" : undefined,
            "&:hover": { bgcolor: "#333" },
          }}
        >
          {isSubmitting ? "処理中..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmStopModal;
